/**
 * FormattingRulesManagerModal — Krok 7.2.4 kreatora widoków list.
 *
 * Otwierany z menu kontekstowego nagłówka kolumny → Conditional Formatting →
 * „Zarządzaj regułami…". Pokazuje listę wszystkich reguł formatowania na
 * bieżącej kolumnie + przyciski do dodania nowej / edycji / usunięcia / zmiany
 * priorytetu (move up/down) / toggle enabled.
 *
 * Dla MVP bez drag-drop — buttons ↑↓ wystarczają (drag-drop dnd-kit można
 * dorzucić w kolejnej iteracji).
 *
 * Edit/New otwiera FormattingRuleEditorModal w odpowiednim trybie (init vs
 * preset). Po zapisaniu reguły wraca tu, parent (ConfigurableTable) propaguje
 * zmiany do `formattingRules` state i ewentualnie do saveLayout.
 */

import { useState } from 'react';
import type { FormattingRule, FormattingRuleType } from '@saas/shared';
import { ArrowDown, ArrowUp, Pencil, Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';

import { FormattingRuleEditorModal } from './FormattingRuleEditorModal';
import type { ColumnDefinition } from './types';

const RULE_TYPE_LABELS: Record<FormattingRuleType, string> = {
  highlight_cells: 'Komórki spełniające warunek',
  top_bottom: 'Górne / dolne wartości',
  above_below_avg: 'Powyżej / poniżej średniej',
  unique_duplicate: 'Unikalne / Duplikaty',
  changing_values: 'Zmieniające się wartości',
  all_cells: 'Wszystkie komórki (gradient)',
  formula: 'Formuła',
};

export interface FormattingRulesManagerModalProps<T = unknown> {
  open: boolean;
  columnId: string;
  columnLabel: string;
  availableColumns: ColumnDefinition<T>[];
  /** Wszystkie reguły z listConfig.formattingRules. Manager filtruje po columnId. */
  rules: FormattingRule[];
  /** Wywoływane gdy lista reguł się zmienia (dodanie, edycja, delete, reorder, toggle). */
  onChange: (next: FormattingRule[]) => void;
  onClose: () => void;
}

export function FormattingRulesManagerModal<T>({
  open,
  columnId,
  columnLabel,
  availableColumns,
  rules,
  onChange,
  onClose,
}: FormattingRulesManagerModalProps<T>): React.ReactElement | null {
  const [editingRule, setEditingRule] = useState<FormattingRule | null>(null);
  const [creating, setCreating] = useState(false);

  const columnRules = rules
    .filter((r) => r.columnId === columnId)
    .sort((a, b) => a.priority - b.priority);

  function move(rule: FormattingRule, direction: 'up' | 'down') {
    const idx = columnRules.indexOf(rule);
    const swapWith = direction === 'up' ? idx - 1 : idx + 1;
    if (swapWith < 0 || swapWith >= columnRules.length) return;
    const a = columnRules[idx]!;
    const b = columnRules[swapWith]!;
    const next = rules.map((r) => {
      if (r.id === a.id) return { ...r, priority: b.priority };
      if (r.id === b.id) return { ...r, priority: a.priority };
      return r;
    });
    onChange(next);
  }

  function toggleEnabled(rule: FormattingRule) {
    onChange(rules.map((r) => (r.id === rule.id ? { ...r, enabled: !r.enabled } : r)));
  }

  function deleteRule(rule: FormattingRule) {
    onChange(rules.filter((r) => r.id !== rule.id));
  }

  function handleEditorSave(rule: FormattingRule) {
    const exists = rules.some((r) => r.id === rule.id);
    if (exists) {
      onChange(rules.map((r) => (r.id === rule.id ? rule : r)));
    } else {
      // Nowa reguła — priority = max(existing) + 1.
      const maxPriority = columnRules.reduce((m, r) => Math.max(m, r.priority), -1);
      onChange([...rules, { ...rule, priority: maxPriority + 1 } as FormattingRule]);
    }
    setEditingRule(null);
    setCreating(false);
  }

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        size="xl"
        title="Reguły formatowania"
        description={`Kolumna: ${columnLabel}`}
        testId="fmt-rules-manager"
        footer={
          <div className="flex w-full items-center justify-between">
            <Button
              variant="secondary"
              onClick={() => setCreating(true)}
              leftIcon={<Plus size={14} aria-hidden="true" />}
            >
              Nowa reguła
            </Button>
            <Button onClick={onClose}>Gotowe</Button>
          </div>
        }
      >
        {columnRules.length === 0 ? (
          <div className="rounded border border-dashed border-gray-300 py-8 text-center text-sm text-text-tertiary dark:border-gray-600">
            Brak reguł na tej kolumnie. Kliknij „Nowa reguła" żeby dodać pierwszą.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-text-tertiary">
              <tr>
                <th className="px-2 py-1 text-left">Aktywna</th>
                <th className="px-2 py-1 text-left">Nazwa</th>
                <th className="px-2 py-1 text-left">Typ</th>
                <th className="px-2 py-1 text-left">Zakres</th>
                <th className="px-2 py-1 text-right">Akcje</th>
              </tr>
            </thead>
            <tbody>
              {columnRules.map((rule, idx) => (
                <tr
                  key={rule.id}
                  className="border-t border-gray-100 dark:border-gray-700"
                  data-testid={`fmt-rule-row-${rule.id}`}
                >
                  <td className="px-2 py-2">
                    <input
                      type="checkbox"
                      checked={rule.enabled}
                      onChange={() => toggleEnabled(rule)}
                      aria-label={rule.enabled ? 'Wyłącz regułę' : 'Włącz regułę'}
                    />
                  </td>
                  <td className="px-2 py-2 text-text-primary">
                    {rule.name || <span className="text-text-tertiary italic">bez nazwy</span>}
                  </td>
                  <td className="px-2 py-2 text-text-secondary">{RULE_TYPE_LABELS[rule.type]}</td>
                  <td className="px-2 py-2 text-text-secondary">
                    {rule.applyTo === 'cell' ? 'Komórka' : 'Wiersz'}
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => move(rule, 'up')}
                        disabled={idx === 0}
                        className="rounded p-1 text-text-tertiary hover:bg-bg-secondary hover:text-text-primary disabled:opacity-30"
                        aria-label="W górę"
                        title="Wyższy priorytet"
                      >
                        <ArrowUp size={14} aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        onClick={() => move(rule, 'down')}
                        disabled={idx === columnRules.length - 1}
                        className="rounded p-1 text-text-tertiary hover:bg-bg-secondary hover:text-text-primary disabled:opacity-30"
                        aria-label="W dół"
                        title="Niższy priorytet"
                      >
                        <ArrowDown size={14} aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingRule(rule)}
                        className="rounded p-1 text-text-tertiary hover:bg-accent-50 hover:text-accent-700 dark:hover:bg-accent-900/20"
                        aria-label="Edytuj regułę"
                      >
                        <Pencil size={14} aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteRule(rule)}
                        className="rounded p-1 text-text-tertiary hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
                        aria-label="Usuń regułę"
                      >
                        <Trash2 size={14} aria-hidden="true" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Modal>

      {/* Editor modal — otwierany dla edycji lub nowej reguły. */}
      {(editingRule || creating) && (
        <FormattingRuleEditorModal
          open={true}
          columnId={columnId}
          columnLabel={columnLabel}
          availableColumns={availableColumns}
          initialRule={editingRule ?? undefined}
          onSave={handleEditorSave}
          onClose={() => {
            setEditingRule(null);
            setCreating(false);
          }}
        />
      )}
    </>
  );
}
